import { Select as BaseSelect } from "@base-ui/react/select";
import { CaretDownIcon, CheckIcon } from "@phosphor-icons/react";
import type { AriaAttributes, KeyboardEvent } from "react";

export interface SelectOption {
  value: string;
  label: string;
  /** One muted line under the label in the menu, for choices whose names alone are not enough; the trigger shows only the label. */
  description?: string;
  disabled?: boolean;
}

// Typeahead and navigation keys belong to the select, not page shortcuts.
function keepSelectKeys(event: KeyboardEvent<HTMLElement>) {
  if (!event.metaKey && !event.ctrlKey && !event.altKey) event.stopPropagation();
}

export interface SelectProps extends Pick<AriaAttributes, "aria-describedby" | "aria-labelledby" | "aria-invalid"> {
  label?: string;
  value: string;
  options: readonly SelectOption[];
  onValueChange: (value: string) => void;
  disabled?: boolean;
  variant?: "compact" | "form" | "property";
  id?: string;
  name?: string;
  required?: boolean;
  autoFocus?: boolean;
  title?: string;
}

/** Shared single choice for filters, forms and properties (design system §0.8). */
export function Select({ label, value, options, onValueChange, disabled, variant = "compact", id, name, required, autoFocus, title, ...aria }: SelectProps) {
  const selected = options.find((option) => option.value === value);
  return <BaseSelect.Root<string> value={value} items={options} disabled={disabled} modal={false} id={id} name={name} required={required}
    onValueChange={(next) => { if (next !== null) onValueChange(next); }}>
    <BaseSelect.Trigger className={`z-select z-select-trigger z-select-${variant}`} aria-label={label} {...aria} autoFocus={autoFocus}
      title={title ?? selected?.label} onKeyDown={keepSelectKeys}>
      <BaseSelect.Value className="z-select-value">{selected ? selected.label || "\u2013" : label || "\u2013"}</BaseSelect.Value>
      <BaseSelect.Icon className="z-select-icon"><CaretDownIcon size={16} aria-hidden /></BaseSelect.Icon>
    </BaseSelect.Trigger>
    <BaseSelect.Portal>
      <BaseSelect.Positioner className="z-select-positioner" side="bottom" align="start" sideOffset={4} collisionPadding={8} alignItemWithTrigger={false}>
        <BaseSelect.Popup className="z-layer z-select-popup" onKeyDown={keepSelectKeys}>
          <BaseSelect.List className="z-select-list">
            {options.map((option) => <BaseSelect.Item key={option.value} value={option.value} label={option.label} disabled={option.disabled} className="z-select-item">
              <span className="z-select-check" aria-hidden><BaseSelect.ItemIndicator><CheckIcon size={16} /></BaseSelect.ItemIndicator></span>
              {option.description
                ? <span className="z-select-item-text"><BaseSelect.ItemText>{option.label || "\u2013"}</BaseSelect.ItemText><span className="z-select-description">{option.description}</span></span>
                : <BaseSelect.ItemText>{option.label || "\u2013"}</BaseSelect.ItemText>}
            </BaseSelect.Item>)}
          </BaseSelect.List>
        </BaseSelect.Popup>
      </BaseSelect.Positioner>
    </BaseSelect.Portal>
  </BaseSelect.Root>;
}
